import { useAuth0 } from "@auth0/auth0-react";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import LogoutIcon from "@mui/icons-material/Logout";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import RefreshIcon from "@mui/icons-material/Refresh";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import React from "react";

import { COMMON_COPY } from "copy/common";
import { useCopy } from "i18n/useCopy";

const PAGE_BACKGROUND_SX: SxProps<Theme> = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  bgcolor: "background.default",
  backgroundImage:
    "radial-gradient(circle at 20% 20%, rgba(74, 144, 226, 0.08), transparent 30%), radial-gradient(circle at 80% 0%, rgba(255, 99, 132, 0.08), transparent 25%)",
  px: { xs: 2, md: 3 },
  py: { xs: 6, md: 10 },
};

const CARD_SX: SxProps<Theme> = {
  position: "relative",
  maxWidth: 820,
  mx: "auto",
  px: { xs: 3, md: 5 },
  py: { xs: 4, md: 6 },
  borderRadius: 3,
  boxShadow: 8,
  border: "1px solid",
  borderColor: "divider",
};

const EYEBROW_SX: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 0.5,
  mb: 2,
  borderRadius: 2,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  bgcolor: "primary.light",
  color: "primary.contrastText",
};

const ICON_BADGE_SX: SxProps<Theme> = {
  width: 64,
  height: 64,
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  bgcolor: "primary.light",
  color: "primary.main",
  boxShadow: 3,
};

const ACTIONS_STACK_SX: SxProps<Theme> = {
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "flex-start",
  gap: 2,
};

const DEV_ALERT_SX: SxProps<Theme> = {
  mt: 3,
  borderRadius: 2,
  border: "1px dashed",
  borderColor: "divider",
  bgcolor: "background.paper",
};

/**
 * Props for `BackendUnavailablePage`.
 */
export interface BackendUnavailablePageProps {
  /**
   * Error thrown when attempting to communicate with the backend.
   */
  error: Error;
  /**
   * The email address of the currently authenticated user (if available).
   */
  userEmail?: string;
  /**
   * The unique identifier of the currently authenticated user (if available).
   */
  userId?: string;
}

/**
 * Full-page message shown when the backend server cannot be reached.
 * This is typically rendered when initial session verification fails.
 */
const HOME_PATH = "/muggleTestV0";
const STATUS_PATH = "/muggleTestV0/health";

export const BackendUnavailablePage: React.FC<BackendUnavailablePageProps> = ({
  error,
  userEmail,
  userId,
}: BackendUnavailablePageProps): React.ReactElement => {
  const copy = useCopy();
  const { logout } = useAuth0();

  /**
   * Reloads the current page so that the app can retry connecting to the backend.
   */
  const handleReloadClick = (): void => {
    window.location.reload();
  };

  /**
   * Signs the user out and redirects to the home page.
   */
  const handleSignOutClick = (): void => {
    logout({
      logoutParams: { returnTo: window.location.origin + HOME_PATH },
    });
  };

  return (
    <Box sx={PAGE_BACKGROUND_SX}>
      <Container maxWidth="md">
        <Paper elevation={4} sx={CARD_SX}>
          <Box sx={EYEBROW_SX}>
            <SupportAgentIcon fontSize="small" />
            <span>{copy.text(COMMON_COPY.backendUnavailable.eyebrow)}</span>
          </Box>

          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Box sx={ICON_BADGE_SX}>
                <CloudOffIcon fontSize="large" />
              </Box>

              <Box>
                <Typography variant="h4" gutterBottom>
                  {copy.text(COMMON_COPY.backendUnavailable.title)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {copy.text(COMMON_COPY.backendUnavailable.description)}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Typography variant="subtitle1">
                {copy.text(COMMON_COPY.backendUnavailable.suggestionsHeading)}
              </Typography>
              <Stack component="ul" spacing={1} sx={{ pl: 2.5, listStyle: "disc" }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  {copy.text(COMMON_COPY.backendUnavailable.suggestions.retryLater)}
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  {copy.text(COMMON_COPY.backendUnavailable.suggestions.checkStatusPage)}
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  {copy.text(COMMON_COPY.backendUnavailable.suggestions.checkNetwork)}
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  {copy.text(COMMON_COPY.backendUnavailable.suggestions.contactAdmin)}
                </Typography>
              </Stack>
            </Stack>

            <Stack sx={ACTIONS_STACK_SX}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleReloadClick}
                fullWidth
                sx={{ maxWidth: { xs: "100%", sm: 220 } }}
              >
                {copy.text(COMMON_COPY.backendUnavailable.actions.retryConnection)}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<MonitorHeartIcon />}
                href={STATUS_PATH}
                fullWidth
                sx={{ maxWidth: { xs: "100%", sm: 200 } }}
              >
                {copy.text(COMMON_COPY.backendUnavailable.actions.viewStatusPage)}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                href="/muggleTestV0/dashboard"
                fullWidth
                sx={{ maxWidth: { xs: "100%", sm: 180 } }}
              >
                {copy.text(COMMON_COPY.backendUnavailable.actions.returnHome)}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleSignOutClick}
                fullWidth
                sx={{ maxWidth: { xs: "100%", sm: 180 } }}
              >
                {copy.text(COMMON_COPY.backendUnavailable.actions.signOut)}
              </Button>
            </Stack>

            <Alert severity="info" variant="outlined" sx={DEV_ALERT_SX}>
              <Typography variant="subtitle2">
                {copy.text(COMMON_COPY.backendUnavailable.errorDetailsHeading)}
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" }}
              >
                {error.message}
              </Typography>
              {(userEmail || userId) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {copy.text(COMMON_COPY.backendUnavailable.accountInfoHeading)}
                  </Typography>
                  {userEmail && (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {copy.format({ text: COMMON_COPY.backendUnavailable.email, params: { email: userEmail } })}
                    </Typography>
                  )}
                  {userId && (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {copy.format({ text: COMMON_COPY.backendUnavailable.userId, params: { userId: userId } })}
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default BackendUnavailablePage;
